import React from "react";
import UserChip from "./atoms/UserChip";
import {
  getStatus, getPriority, getType, userName, formatDay, relativeTime, formatDateTime,
} from "./taskConstants";

// Geçmiş sekmesi — TaskDetail.activity (yeniden eskiye) satırları.
// Activity tipi: contract "## Ortak tipler"; ref alanlarda from/to { _id, name }.

const NONE = "yok";

const refName = (v) => {
  if (v == null || v === "") return null;
  if (typeof v === "object") return (v.lastName !== undefined ? userName(v) : v.name) || "";
  return String(v);
};
const q = (v) => `"${v}"`;
// Kontrol listesi maddesi: { _id, text } | { text, done } | string
const itemText = (v) => {
  if (v == null || v === "" || typeof v === "boolean") return null;
  if (typeof v === "object") return v.text || v.name || null;
  return String(v);
};
const listNames = (v) => {
  const names = (Array.isArray(v) ? v : []).map(refName).filter(Boolean);
  return names.length ? names.join(", ") : NONE;
};

// field_changed: alan -> [cümledeki ad, değer biçimleyici]
const FIELDS = {
  title: ["başlığı", (v) => (v ? q(v) : NONE)],
  priority: ["önceliği", (v) => getPriority(v)?.label || v || NONE],
  type: ["türü", (v) => getType(v)?.label || v || NONE],
  status: ["durumu", (v) => getStatus(v)?.label || v || NONE],
  dueDate: ["bitiş tarihini", (v) => formatDay(v) || NONE],
  startDate: ["başlangıç tarihini", (v) => formatDay(v) || NONE],
  labels: ["etiketleri", listNames],
  editors: ["düzenleyenleri", listNames],
  relatedDevice: ["ilgili cihazı", (v) => (v ? refName(v) || "bir cihaz" : NONE)],
  relatedFirm: ["ilgili firmayı", (v) => (v ? refName(v) || "bir firma" : NONE)],
};

/**
 * Activity -> Türkçe cümle (aktör adıyla). Saf fonksiyon.
 * ör. "Ali Kaya önceliği Orta → Yüksek yaptı"
 */
export function describeActivity(a) {
  if (!a) return "";
  const who = userName(a.actor) || "Biri";
  switch (a.type) {
    case "created":
      return `${who} işi oluşturdu`;
    case "status_changed":
      return `${who} durumu ${FIELDS.status[1](a.from)} → ${FIELDS.status[1](a.to)} yaptı`;
    case "assigned": {
      // 1.8 — from/to kişi listesi; eski kayıtlarda tek kişi.
      if (Array.isArray(a.from) || Array.isArray(a.to)) {
        const names = (v) => (listNames(v) === NONE ? "atanmamış" : listNames(v));
        return `${who} atananları ${names(a.from)} → ${names(a.to)} yaptı`;
      }
      const from = refName(a.from) || "atanmamış";
      const to = refName(a.to) || "atanmamış";
      return `${who} atananı ${from} → ${to} yaptı`;
    }
    case "field_changed": {
      if (a.field === "description") return `${who} açıklamayı güncelledi`;
      const spec = FIELDS[a.field];
      if (!spec) return `${who} ${a.field || "bir alanı"} güncelledi`;
      const [name, fmt] = spec;
      return `${who} ${name} ${fmt(a.from)} → ${fmt(a.to)} yaptı`;
    }
    case "comment_added":
      return `${who} yorum ekledi`;
    case "attachment_added":
      return `${who} ${q(refName(a.to) || "dosya")} dosyasını ekledi`;
    case "attachment_removed":
      return `${who} ${q(refName(a.from) || "dosya")} dosyasını kaldırdı`;
    case "watcher_added":
      return `${who} işi takip etmeye başladı`;
    case "watcher_removed":
      return `${who} işi takip etmeyi bıraktı`;
    case "deleted":
      return `${who} işi sildi`;
    case "subtask_added": {
      const to = a.to && typeof a.to === "object" ? a.to : null;
      const name = to ? to.key || to.name || to.title : refName(a.to);
      return `${who} alt iş ekledi: ${name || "alt iş"}`;
    }
    case "checklist_added":
      return `${who} kontrol listesine madde ekledi: ${q(itemText(a.to) || "madde")}`;
    case "checklist_removed":
      return `${who} kontrol listesinden maddeyi kaldırdı: ${q(itemText(a.from) || itemText(a.to) || "madde")}`;
    case "checklist_toggled": {
      const text = itemText(a.to) || itemText(a.from) || "madde";
      const done = typeof a.to === "boolean" ? a.to : Boolean(a.to && a.to.done);
      return `${who} ${q(text)} maddesini ${done ? "işaretledi" : "işaretini kaldırdı"}`;
    }
    default:
      return `${who} işi güncelledi`;
  }
}

function TaskActivity({ activity = [] }) {
  if (!activity.length) {
    return <p className="text-sm text-gray-500 py-4">Henüz geçmiş kaydı yok.</p>;
  }
  return (
    <ul className="space-y-3" data-testid="task-activity">
      {activity.map((a) => (
        <li key={a._id} className="flex items-start gap-2">
          <UserChip user={a.actor} size="sm" showName={false} />
          <p className="text-sm text-gray-700 min-w-0">
            {describeActivity(a)}
            <span className="text-xs text-gray-400" title={formatDateTime(a.createdAt)}>
              {" · "}
              {relativeTime(a.createdAt)}
            </span>
          </p>
        </li>
      ))}
    </ul>
  );
}

export default TaskActivity;
